import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { take, takeUntil, takeWhile } from 'rxjs/operators';
import { Unit, UnitService } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { DisplayData, FinancialGeneralService } from './financial-general.service';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { LooseObject } from '../../../models/loose-object.model';
import { PlotlyTrace } from '../../../models/results/plotly.model';

@Component({
  selector: 'app-baseline-financial-general',
  templateUrl: './baseline-financial-general.component.html',
  styleUrls: ['./baseline-financial-general.component.scss']
})
export class BaselineFinancialGeneralComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {

  private onDestroyNotifier = new EventEmitter();

  categories: FinancialCategory[] = [];

  selectedCategory?: FinancialCategory;
  selectedCurrency?: Unit | null = null;

  currencyUnits: Unit[] = [];
  currencyUnitsLoaded = false;

  displayGraph = false;
  displayTable = false;

  graphData?: PlotlyTrace[] = [];
  graphType: string | null = null;
  currencyLabel: string = '';
  periodLabel: string = '';

  /**
   * An array of graph data for the default overview graphs
   */
  overviewGraphData: OverviewGraphData[] = [];

  tableData?: LooseObject[] = [];
  tableHeaders?: string[] = [];

  exchangeRateDate?: string;

  unitServiceReady = false;
  resultsLoaded = false;
  CurrencyLoadedNotifier = new EventEmitter();
  /**
   * The custom legend that will be used for the default overview graphs
   */
  legend = {x: 1.2, y: 0.5};

  trigger?: string;

  constructor(
    private projectCommService: ProjectCommunicationService,
    private financialGeneralService: FinancialGeneralService,
    private toastrService: ConfiguredToastrService,
    private unitService: UnitService,
    private translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setTranslations();
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        this.resultsLoaded = loaded;
        if (loaded) {
          this.setup();
        }
      });

    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((ready: boolean) => {
        this.unitServiceReady = ready;
        if (ready) {
          this.setup();
        }
      });
    this.CurrencyLoadedNotifier.pipe(take(1)).subscribe(() => {
      this.setDefaultGraphData();
    });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.setTranslations();
        this.setup();
        this.trigger = language;
      });
  }

  setTranslations() {
    this.periodLabel = this.translationService.get('RESULTS.GENERAL.YEARS');
    this.setCurrencyLabel();
  }

  setup() {
    if (this.unitServiceReady && this.resultsLoaded) {
      this.setResults();
      if (this.categories && this.categories.length > 0) {
        // this.updateCategories();
      } else {
        this.setCategories();
      }

      this.setDefaultGraphData();
    }
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  getFinExchange(created: string | null) {
    this.unitService.getCurrencyUnits(created).then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.currencyUnits = units;

        // sort rates alphabetically
        this.currencyUnits.sort((first: Unit, second: Unit) => {
          if (Number(first.order) < Number(second.order)) {
            return -1;
          } else if (Number(second.order) < Number(first.order)) {
            return 1;
          } else {
            return 0;
          }
        });
        // Select default unit if set or CAD otherwise
        if (this.projectCommService.getProjectDefaults().currency != null) {
          this.selectedCurrency = this.unitService.getUnitById(this.projectCommService.getProjectDefaults().currency);
        } else {
          this.selectedCurrency = this.currencyUnits.find(currency => currency.displayName === 'CAD');
        }
        this.currencyUnitsLoaded = true;
        this.CurrencyLoadedNotifier.emit();
        this.setDisplayData();
      }
    );
  }

  private setResults() {
    const results = this.projectCommService.getResults();
    if (results && results.financial) {
      this.getFinExchange(results.created);
    } else {
      this.toastrService.warning(this.translationService.get('RESULTS.ERRORS.PLEASE_SIMULATE'),
        this.translationService.get('RESULTS.ERRORS.NO_RESULTS'));
    }
  }

  private setCategories() {
    this.categories = [
      {name: 'overview', translationId: 'RESULTS.SECTIONS.OVERVIEW', graphType: null, dataFunc: null},
      {
        name: 'opex breakdown', translationId: 'RESULTS.SECTIONS.OPEX_BREAKDOWN', graphType: 'piechart',
        dataFunc: this.financialGeneralService.getOpexBreakdown.bind(this)
      },
      {
        name: 'capex breakdown', translationId: 'RESULTS.SECTIONS.CAPEX_BREAKDOWN', graphType: 'piechart',
        dataFunc: this.financialGeneralService.getCapexBreakdown.bind(this)
      },
      {
        name: 'revenues breakdown', translationId: 'RESULTS.SECTIONS.REVENUES_BREAKDOWN', graphType: 'piechart',
        dataFunc: this.financialGeneralService.getRevenuesBreakdown.bind(this)
      },
      {
        name: 'selling products revenue breakdown', translationId: 'RESULTS.SECTIONS.SELLING_PRODUCTS_REVENUE_BREAKDOWN',
        graphType: 'piechart', dataFunc: this.financialGeneralService.getSellingProductsRevenueBreakdown.bind(this)
      },
      {
        name: 'feedstock revenue breakdown', translationId: 'RESULTS.SECTIONS.FEEDSTOCK_REVENUE_BREAKDOWN',
        graphType: 'piechart', dataFunc: this.financialGeneralService.getFeedstockRevenueBreakdown.bind(this)
      },
      {
        name: 'additional income breakdown', translationId: 'RESULTS.SECTIONS.ADDITIONAL_INCOME_BREAKDOWN',
        graphType: 'piechart', dataFunc: this.financialGeneralService.getAdditionalIncomeBreakdown.bind(this)
      },
      {
        name: 'revenue trend', translationId: 'RESULTS.SECTIONS.REVENUE_TREND', graphType: 'stackedbar',
        dataFunc: this.financialGeneralService.getRevenueTrend.bind(this)
      },
      {
        name: 'earnings trend', translationId: 'RESULTS.SECTIONS.EARNINGS_TREND', graphType: 'bargroup',
        dataFunc: this.financialGeneralService.getEarningsTrend.bind(this)
      },
    ];

    // Default selection
    if (!this.selectedCategory) {
      this.selectedCategory = this.categories.find(cat => cat.name === 'overview');
    }
  }

  /**
   * Sets the data to be dislayed
   */
  setDisplayData(): void {
    this.graphData = [];
    this.tableData = [];

    this.displayGraph = false;
    this.displayTable = false;

    if (this.selectedCurrency == null || this.selectedCategory == null ||
      !this.selectedCategory.graphType || !this.selectedCategory.dataFunc
    ) {
      return;
    }

    this.graphType = this.selectedCategory.graphType;
    const displayData: DisplayData = this.selectedCategory.dataFunc(this.selectedCurrency);

    if (displayData.hasGraphData) {
      this.graphData = displayData.graphData;
      this.currencyLabel = this.translationService.get('RESULTS.GENERAL.AVERAGE') + ' (' +
        this.translationService.get(this.selectedCurrency.translationId) + ')';
    }
    if (displayData.hasTableData) {
      this.tableData = displayData.tableData;
      this.tableHeaders = displayData.tableHeaders;
    }

    setTimeout(() => {
      this.displayGraph = displayData.hasGraphData && this.selectedCurrency != null;
      this.displayTable = displayData.hasTableData && this.selectedCurrency != null;
    });
  }

  /**
   * Populate the default graph overview data object with data.
   */
  setDefaultGraphData() {
    this.overviewGraphData = [];
    if (!this.unitServiceReady || !this.resultsLoaded || !this.currencyUnitsLoaded ||
      !this.selectedCategory || this.selectedCategory.name !== 'overview'
    ) {
      return;
    }
    const tempData: OverviewGraphData[] = [];
    const locations: number[] = [1, 2, 3, 4];

    this.setCurrencyLabel();

    for (const location of locations) {
      if (this.categories[location].dataFunc) {
        const dataFunc = this.categories[location].dataFunc as Function;
        const displayData: DisplayData = dataFunc(this.selectedCurrency, true);
        if (displayData.hasGraphData) {
          const graphData = displayData.graphData;
          tempData.push({
            name: this.categories[location].name,
            translationId: this.categories[location].translationId,
            graphData: graphData
          });
        }
      }
    }
    setTimeout(() => {
      this.overviewGraphData = tempData;
    });
  }

  private setCurrencyLabel() {
    if (this.selectedCurrency) {
      this.currencyLabel = this.translationService.get('RESULTS.GENERAL.AVERAGE') + ' (' +
        this.translationService.get(this.selectedCurrency.translationId) + ')';
    }
  }
}

interface FinancialCategory {
  name: string,
  translationId: string,
  graphType: string | null,
  dataFunc: Function | null
}

interface OverviewGraphData {
  name: string,
  translationId: string,
  graphData?: PlotlyTrace[]
}